// libraries.
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { RootState } from '@store';

// types.
import type { PolygonType } from '../../polygon-styles';

export type PolygonTypeFilter = PolygonType | 'all';

export interface Polygon {
  comment?: string;
  polygon: google.maps.LatLngLiteral[];
  polygon_id?: string;
  polygon_type?: 'standard' | 'blocking';
  property_type_id?: string;
  property_type?: string;
}
export interface PolygonToolState {
  isModalOpen: boolean;
  isDrawing: boolean;
  polygons: Polygon[];
  polygonTypeFilter: PolygonTypeFilter;
}

export const initialState: PolygonToolState = {
  isModalOpen: false,
  isDrawing: false,
  polygons: [],
  polygonTypeFilter: 'all',
};

// slice.
export const domainPolygonToolSlice = createSlice({
  name: 'domainPolygonTool',
  initialState,
  reducers: {
    startDrawing: state => {
      state.isDrawing = true;
    },
    cancelDrawing: state => {
      state.isDrawing = false;
    },
    createPolygon: (state, action: PayloadAction<Polygon>) => {
      state.polygons.push(action.payload);
      state.isModalOpen = true;
    },
    deletePolygon: state => {
      state.polygons = [];
    },
    closeModal: state => {
      state.isModalOpen = false;
    },
    setComment: (
      state,
      action: PayloadAction<{ index: number; comment: string }>
    ) => {
      const { index, comment } = action.payload;
      if (state.polygons[index]) {
        state.polygons[index].comment = comment;
      }
    },
    setPropertyType: (
      state,
      action: PayloadAction<{
        index: number;
        property_type_id: string;
        property_type: string;
      }>
    ) => {
      const { index, property_type_id, property_type } = action.payload;
      if (state.polygons[index]) {
        state.polygons[index].property_type_id = property_type_id;
        state.polygons[index].property_type = property_type;
      }
    },
    setPolygonType: (
      state,
      action: PayloadAction<{
        index: number;
        polygon_type: 'standard' | 'blocking';
      }>
    ) => {
      const { index, polygon_type } = action.payload;
      if (state.polygons[index]) {
        state.polygons[index].polygon_type = polygon_type;
      }
    },
    setPolygonTypeFilter: (
      state,
      action: PayloadAction<PolygonTypeFilter>
    ) => {
      state.polygonTypeFilter = action.payload;
    },
    setDefaultState: () => {
      return initialState;
    },
  },
});

export const {
  startDrawing,
  cancelDrawing,
  createPolygon,
  deletePolygon,
  closeModal,
  setComment,
  setPolygonType,
  setPolygonTypeFilter,
  setPropertyType,
  setDefaultState,
} = domainPolygonToolSlice.actions;

export default domainPolygonToolSlice.reducer;

// selectors.
export const getIsDrawingPolygon = (state: RootState) =>
  state.domainPolygonTool.isDrawing;

export const getPolygons = (state: RootState) =>
  state.domainPolygonTool.polygons;

export const getIsModalOpen = (state: RootState) =>
  state.domainPolygonTool.isModalOpen;

export const getPolygonTypeFilter = (state: RootState) =>
  state.domainPolygonTool.polygonTypeFilter;
